import PropTypes from 'prop-types';
import { PLCCHolderRoles } from 'modules/clients/constants';

export const userPropTypes = PropTypes.shape({
  logged: PropTypes.bool.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  employeeNumber: PropTypes.string,
  nmid: PropTypes.string,
  email: PropTypes.string,
  role: PropTypes.string,
  brand: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.string),
  isSuperUser: PropTypes.bool,
  contact: PropTypes.string,
  storeNumber: PropTypes.string,
  storeLocation: PropTypes.string,
  pin: PropTypes.string,
});

export const ProductPropTypes = PropTypes.shape({
  itemskuId: PropTypes.string,
  cmosskuId: PropTypes.string,
  color: PropTypes.string,
  imgUrl: PropTypes.string,
  inventory: PropTypes.number,
  price: PropTypes.oneOfType([
    PropTypes.shape({
      currencyCode: PropTypes.string,
      currentPrice: PropTypes.string,
      itemPriceEach: PropTypes.string,
      itemPriceExt: PropTypes.string,
      itemTax: PropTypes.string,
    }),
    PropTypes.number,
  ]),
  name: PropTypes.string,
  size: PropTypes.string,
  brand: PropTypes.string,
  isLoading: PropTypes.bool,
});

export const ShipmentMethodPropTypes = PropTypes.shape({
  id: PropTypes.string,
  name: PropTypes.string,
  term: PropTypes.string,
  subTotalPrice: PropTypes.string,
  totalPrice: PropTypes.string,
  taxAmount: PropTypes.string,
  deliveryCharges: PropTypes.string,
  orderTotalWithGiftCard: PropTypes.string,
});

export const AddressPropTypes = PropTypes.shape({
  addressLine1: PropTypes.string,
  addressLine2: PropTypes.string,
  city: PropTypes.string,
  country: PropTypes.string,
  dayTelephone: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  line1: PropTypes.string,
  line2: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  verificationFlag: PropTypes.number,
  addressType: PropTypes.number,
});

export const ClienDetailsPropTypes = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  acceptedTC: PropTypes.bool,
  paymentMethods: PropTypes.array,
  shippingAddress: PropTypes.arrayOf(AddressPropTypes),
});

export const paymentInfoPropTypes = PropTypes.shape({
  maskedNumber: PropTypes.string,
  networkType: PropTypes.string,
  cardExpMonth: PropTypes.string,
  cardExpYear: PropTypes.string,
  onTimeToken: PropTypes.string,
});

export const PaymentOptionPropTypes = PropTypes.shape({
  cardExpMonth: PropTypes.string,
  cardExpYear: PropTypes.string,
  keyIdentifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maskedNumber: PropTypes.string,
  networkType: PropTypes.string,
  billingAddress: AddressPropTypes,
});

export const GiftCardPaymentOptionPropTypes = PropTypes.shape({
  giftCardNumber: PropTypes.string,
  giftCardPin: PropTypes.string,
  cardType: PropTypes.oneOf(['NEX']),
  giftCardBalance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  selected: PropTypes.bool,
});

export const OrderItemPropTypes = PropTypes.shape({
  cancelCode: PropTypes.string,
  cmosCatalogId: PropTypes.string,
  cmosCatalogItem: PropTypes.string,
  cmosLineItemId: PropTypes.string,
  cmosSkuId: PropTypes.string,
  currentStatusDate: PropTypes.string,
  duty: PropTypes.number,
  externalLineItemId: PropTypes.string,
  freight: PropTypes.number,
  other: PropTypes.number,
  price: PropTypes.oneOfType([
    PropTypes.shape({
      currencyCode: PropTypes.string,
      currentPrice: PropTypes.string,
      itemPriceEach: PropTypes.string,
      itemPriceExt: PropTypes.string,
      itemTax: PropTypes.string,
    }),
    PropTypes.number,
  ]),
  productName: PropTypes.string,
  promotionCode: PropTypes.string,
  quantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  specialInstructions: PropTypes.arrayOf(PropTypes.string),
  status: PropTypes.string,
  storeFulfillFlag: PropTypes.string,
  tax: PropTypes.number,
  transactionType: PropTypes.string,
  serviceLevelCode: PropTypes.string,
});

export const additionalPaymentsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    type: PropTypes.string,
    maskedCardNumber: PropTypes.string,
    amountReceived: PropTypes.string,
  }),
);

export const OrderPropTypes = PropTypes.shape({
  accountId: PropTypes.string,
  cmosOrderNumber: PropTypes.string,
  email: PropTypes.string,
  orderDate: PropTypes.string,
  orderDetails: PropTypes.arrayOf(
    PropTypes.shape({
      items: PropTypes.arrayOf(OrderItemPropTypes),
      shippingAddress: AddressPropTypes,
    }),
  ),
  orderId: PropTypes.string,
  paymentDetails: PropTypes.shape({
    type: PropTypes.string,
    maskedCardNumber: PropTypes.string,
  }),
  additionalPaymentDetails: additionalPaymentsPropType,
  shouldDisplay: PropTypes.bool,
  soldTo: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
});

export const OrderInfoPropType = PropTypes.shape({
  client: PropTypes.string,
  channel: PropTypes.string,
  date: PropTypes.string,
  'order#': PropTypes.string,
  total: PropTypes.string,
});

export const shippingFormValues = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  line1: PropTypes.string,
  line2: PropTypes.string,
  state: PropTypes.string,
  zip: PropTypes.string,
  phone: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  country: PropTypes.string,
});

export const Conversation = PropTypes.shape({
  lastMessageBody: PropTypes.string,
  lastMessageType: PropTypes.oneOf(['SMS', 'EMAIL']),
  associateId: PropTypes.string,
  lastTwilioMessageId: PropTypes.string,
  twilioChannelId: PropTypes.string,
  dateCreated: PropTypes.string,
  externalAddress: PropTypes.string,
  externalEntiryName: PropTypes.string,
  subject: PropTypes.string,
  hasAttachments: PropTypes.bool,
});

export const GroupPropTypes = PropTypes.shape({
  groupId: PropTypes.string,
  description: PropTypes.string,
  name: PropTypes.string,
  coverImageUrl: PropTypes.string,
  clientsCount: PropTypes.number,
  averageAnnualAmount: PropTypes.number,
  creationTime: PropTypes.string,
});

export const ClientNotePropTypes = PropTypes.shape({
  id: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  isPrivate: PropTypes.bool,
  canEdit: PropTypes.bool,
  canDelete: PropTypes.bool,
  author: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    associatePin: PropTypes.string,
  }),
  lastUpdatedTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

export const OnlineProfiles = PropTypes.arrayOf(
  PropTypes.shape({
    email: PropTypes.string,
    phone: PropTypes.string,
    profileId: PropTypes.string,
    profileType: PropTypes.string,
    sourceName: PropTypes.string,
  }),
);

export const ClientProfileData = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  additionalEmail: PropTypes.string,
  additionalPhone: PropTypes.string,
  emails: PropTypes.arrayOf(PropTypes.string),
  onlineProfiles: OnlineProfiles,
  phones: PropTypes.arrayOf(
    PropTypes.shape({
      number: PropTypes.string,
      type: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
    }),
  ),
});

export const ConversationPropTypes = PropTypes.shape({
  friendlyName: PropTypes.string,
  userName: PropTypes.string,
  phone: PropTypes.string,
  contact: PropTypes.string,
});

export const messagePropTypes = PropTypes.shape({
  OutgoingMessageId: PropTypes.string,
  MessageType: PropTypes.string,
  DateCreated: PropTypes.string,
  MessageStatus: PropTypes.string,
  Direction: PropTypes.string,
  Body: PropTypes.string,
  IncomingMessageId: PropTypes.string,
});

export const AlertPropTypes = PropTypes.shape({
  eventId: PropTypes.string,
  associateId: PropTypes.string,
  event_status: PropTypes.string,
  type: PropTypes.string,
  description: PropTypes.string,
  timestamp: PropTypes.number,
  customerName: PropTypes.string,
});

export const ClientCentralPropTypes = PropTypes.shape({
  connectedWithMe: PropTypes.bool,
  profileId: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phoneNumber: PropTypes.string,
  assetsBalance: PropTypes.shape({
    promo: PropTypes.number,
    point: PropTypes.number,
  }),
});

export const ClientProfileLoyaltyAssetPropTypes = PropTypes.shape({
  cardNumber: PropTypes.number,
  cardType: PropTypes.string,
  currentBal: PropTypes.string,
  expDate: PropTypes.string,
  initialBal: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  issueDate: PropTypes.string,
  name: PropTypes.string,
});

export const LoyaltyAccountPropTypes = PropTypes.shape({
  earnedLevelName: PropTypes.string,
  earnedLevel: PropTypes.string,
  currentPoints: PropTypes.string,
  redeemAsofDate: PropTypes.string,
  redeemPtsEarned: PropTypes.string,
  redeemPtsRedeemed: PropTypes.string,
  redeemPtsAvailable: PropTypes.string,
});

export const GroupClientsFiltersPropTypes = PropTypes.shape({
  conciergeItems: PropTypes.bool,
  shoppedFrom: PropTypes.string,
  inCircleLevel: PropTypes.string,
  connectedWithMe: PropTypes.bool,
  contactedTill: PropTypes.string,
  contactedFrom: PropTypes.string,
  shoppedWithMe: PropTypes.bool,
  withAssets: PropTypes.string,
});

export const PLCCCardHolderType = PropTypes.shape({
  cardholderRole: PropTypes.oneOf(Object.values(PLCCHolderRoles)),
  primaryCardholder: PropTypes.shape({
    cmdUri: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
});

export const ReminderPropType = PropTypes.shape({
  id: PropTypes.string,
  body: PropTypes.string,
  completed: PropTypes.bool,
  createdIsoDateTime: PropTypes.string,
  reminderIsoDate: PropTypes.string,
  clients: PropTypes.arrayOf(PropTypes.shape({})),
});

export const ProductsPropTypes = PropTypes.arrayOf(ProductPropTypes);
export const ShipmentMethodsPropTypes = PropTypes.arrayOf(ShipmentMethodPropTypes);
export const AddressesPropTypes = PropTypes.arrayOf(AddressPropTypes);
export const PaymentOptionsPropTypes = PropTypes.arrayOf(PaymentOptionPropTypes);
export const GiftCardPaymentOptionsPropTypes = PropTypes.arrayOf(GiftCardPaymentOptionPropTypes);
export const OrderInfoProptypes = PropTypes.arrayOf(OrderInfoPropType);
export const Conversations = PropTypes.arrayOf(Conversation);
export const clientsCentral = PropTypes.arrayOf(ClientCentralPropTypes);
export const ClientProfileLoyaltyAssetsPropTypes = PropTypes.arrayOf(ClientProfileLoyaltyAssetPropTypes);
